import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import ActiveFilterItemIndicator from '../../components/filters/ActiveFilterItemIndicator';
import Pagination from '../../components/Pagination';
import { TableSkeleton } from '../../components/Skeleton';
import { formatNumber } from '../../lib/numberFormat';
import backend from '../../services/backend';
import { hasActiveFilters, toFilterQuery, type ChatQuestionsFilters } from './chatQuestionsFilters';
import TurnsTable from './TurnsTable';

export const TURNS_PAGE_SIZE = 50;

export default function TurnsTab({
  filters,
  gapKey,
  onClearGapKey,
  page,
  onPageChange,
}: {
  filters: ChatQuestionsFilters;
  gapKey: string | null;
  onClearGapKey: () => void;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const turnsQuery = useQuery({
    queryKey: ['agent-turns', page, filters, gapKey],
    queryFn: () =>
      backend
        .listAgentTurns({
          ...toFilterQuery(filters),
          ...(gapKey && { gapKey }),
          page: String(page),
          limit: String(TURNS_PAGE_SIZE),
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  const items = turnsQuery.data?.items ?? [];
  const total = turnsQuery.data?.total ?? 0;
  const hasNextPage = page * TURNS_PAGE_SIZE < total;

  return (
    <div className="flex flex-col gap-4">
      {(gapKey || turnsQuery.data) && (
        <div className="flex flex-wrap items-center gap-3">
          {gapKey && (
            <ActiveFilterItemIndicator
              label="Gap"
              value={gapKey}
              onRemove={onClearGapKey}
              className="whitespace-normal"
            />
          )}
          {turnsQuery.data && (
            <span className="text-sm text-gray-500 dark:text-zinc-400">
              {formatNumber(total)} {total === 1 ? 'turn' : 'turns'}
            </span>
          )}
        </div>
      )}

      {turnsQuery.isLoading && <TableSkeleton />}

      {turnsQuery.error && (
        <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">
          {(turnsQuery.error as Error).message}
        </div>
      )}

      {!turnsQuery.isLoading && !turnsQuery.error && (
        <div className="flex flex-col gap-2">
          <div
            className={clsx(
              'bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden transition-opacity',
              turnsQuery.isPlaceholderData && 'opacity-50 pointer-events-none',
            )}
          >
            {items.length > 0 ? (
              <TurnsTable turns={items} />
            ) : (
              <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
                {page > 1
                  ? 'No turns on this page.'
                  : gapKey || hasActiveFilters(filters)
                    ? 'No chat turns match these filters.'
                    : 'No chat turns recorded yet.'}
              </p>
            )}
          </div>

          {(page > 1 || hasNextPage) && (
            <Pagination page={page} handlePageChange={onPageChange} hasNextPage={hasNextPage} />
          )}
        </div>
      )}
    </div>
  );
}
